import { useEffect, useRef } from 'react';

import TabBar from './components/TabBar';
import { StoreProvider, useStore } from './store';
import { primeSpeech } from './tts';
import ChatScreen from './screens/ChatScreen';
import HomeScreen from './screens/HomeScreen';
import PracticeScreen from './screens/PracticeScreen';
import ProgressScreen from './screens/ProgressScreen';

function Router() {
  const s = useStore();
  const inChat = s.activeScenario !== null;

  // ---- browser Back button ↔ in-app navigation ----------------------------
  // The Android build closed a conversation with the hardware back button via
  // `BackHandler`. On the web the equivalent is a history entry: entering a
  // conversation pushes one, and a popstate means the user asked to leave it.
  // The in-app ← button then has to consume the entry it pushed, which would
  // itself fire popstate — hence the short suppression window.
  const cameFromPop = useRef(false);
  const suppressUntil = useRef(0);
  const wasInChat = useRef(false);
  const exit = useRef(s.exitChat);
  exit.current = s.exitChat;
  const inChatRef = useRef(inChat);
  inChatRef.current = inChat;

  useEffect(() => {
    const onPop = () => {
      if (performance.now() < suppressUntil.current) return;
      if (!inChatRef.current) return;
      cameFromPop.current = true;
      exit.current();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    const was = wasInChat.current;
    wasInChat.current = inChat;

    if (inChat && !was) {
      window.history.pushState({ sadie: 'chat' }, '');
    } else if (!inChat && was && !cameFromPop.current) {
      // The ← button, not the browser's: drop the entry we pushed.
      suppressUntil.current = performance.now() + 500;
      window.history.back();
    }
    cameFromPop.current = false;
  }, [inChat]);

  // ---- iOS Safari speech unlock -------------------------------------------
  // Mobile Safari ignores speechSynthesis.speak() unless speech has been
  // started once from a genuine user gesture. The tutor's opener speaks as soon
  // as a conversation begins, so without this the first line would be silent.
  useEffect(() => {
    const prime = () => primeSpeech();
    window.addEventListener('pointerdown', prime, { once: true });
    window.addEventListener('keydown', prime, { once: true });
    return () => {
      window.removeEventListener('pointerdown', prime);
      window.removeEventListener('keydown', prime);
    };
  }, []);

  if (s.activeScenario) {
    return <ChatScreen scenario={s.activeScenario} />;
  }

  return (
    <>
      {!s.voiceSupported && (
        <div className="notice">
          This browser can't speak, so the tutor stays silent. Everything else works — Chrome, Edge
          and Safari have the best voices.
        </div>
      )}
      {s.tab === 'talk' && <HomeScreen />}
      {s.tab === 'practice' && <PracticeScreen />}
      {s.tab === 'progress' && <ProgressScreen />}
      <TabBar />
    </>
  );
}

export default function App() {
  return (
    <StoreProvider>
      <div className="app">
        <Router />
      </div>
    </StoreProvider>
  );
}
